import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { HelpCircle, ExternalLink, AlertTriangle, CheckCircle, XCircle } from "lucide-react";

export const ISDEFAQSection = () => {
  return (
    <section className="py-16 bg-gradient-to-br from-muted/30 to-background">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center mb-4">
              <HelpCircle className="h-8 w-8 text-primary mr-3" />
              <h2 className="text-3xl md:text-4xl font-bold">
                ISDE Subsidie FAQ 2025
              </h2>
            </div>
            <p className="text-lg text-muted-foreground">
              Belangrijke voorwaarden, beperkingen en veelgestelde vragen over de ISDE subsidie
            </p>
          </div>

          {/* Quick Reference Cards */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <Card className="border-destructive/20">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2 text-destructive">
                  <XCircle className="h-5 w-5" />
                  Uitgesloten
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm">
                  <li>• Lucht-lucht warmtepompen</li>
                  <li>• Nieuwbouw na 2019</li>
                  <li>• Zelfstandige uitvoering</li>
                  <li>• Uitbouwen/aanbouw</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-warning/20">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2 text-warning">
                  <AlertTriangle className="h-5 w-5" />
                  Let Op
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm">
                  <li>• A++ label vereist (warmtepomp)</li>
                  <li>• Meldcode checken</li>
                  <li>• 24 maanden termijn</li>
                  <li>• Eigenaar + bewoner</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-success/20">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2 text-success">
                  <CheckCircle className="h-5 w-5" />
                  Vereist
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm">
                  <li>• Erkend installatiebedrijf</li>
                  <li>• Factuur + betaalbewijs</li>
                  <li>• Installatiefoto's</li>
                  <li>• Bestaande oppervlakte</li>
                </ul>
              </CardContent>
            </Card>
          </div>

          {/* Detailed FAQ */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <HelpCircle className="h-6 w-6 text-primary" />
                Veelgestelde Vragen & Voorwaarden
              </CardTitle>
              <CardDescription>
                Uitgebreide informatie over ISDE subsidie voorwaarden en beperkingen
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Accordion type="single" collapsible className="w-full">
                
                <AccordionItem value="warmtepompen-uitsluitingen">
                  <AccordionTrigger className="text-left">
                    <div className="flex items-center gap-2">
                      <Badge variant="destructive" className="text-xs">Uitgesloten</Badge>
                      Welke warmtepompen komen NIET in aanmerking?
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="space-y-4">
                    <Alert>
                      <AlertTriangle className="h-4 w-4" />
                      <AlertDescription>
                        <strong>Lucht-lucht warmtepompen zijn volledig uitgesloten</strong> van ISDE subsidie. 
                        Deze systemen komen onder geen enkele voorwaarde in aanmerking.
                      </AlertDescription>
                    </Alert>
                    <div className="space-y-2">
                      <p><strong>Wel in aanmerking:</strong></p>
                      <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground ml-4">
                        <li>Lucht-water warmtepompen</li>
                        <li>Hybride warmtepompen</li>
                        <li>Bodem-water warmtepompen</li>
                        <li>Water-water warmtepompen</li>
                      </ul>
                    </div>
                    <Button variant="outline" size="sm" asChild>
                      <a href="https://www.rvo.nl/subsidies-financiering/isde" target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="h-4 w-4 mr-2" />
                        Bekijk RVO warmtepomp lijst
                      </a>
                    </Button>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="energielabel-eis">
                  <AccordionTrigger className="text-left">
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary" className="text-xs bg-amber-100 text-amber-800">Vereist</Badge>
                      Welk energielabel is verplicht voor warmtepompen?
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="space-y-4">
                    <Alert>
                      <AlertTriangle className="h-4 w-4" />
                      <AlertDescription>
                        Voor warmtepompen ≤ 70 kW is <strong>minimaal energielabel A++</strong> vereist vanaf 1 januari 2024.
                      </AlertDescription>
                    </Alert>
                    <div className="space-y-3">
                      <div>
                        <h4 className="font-semibold mb-2">Labelvoordelen:</h4>
                        <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground ml-4">
                          <li><strong>A++:</strong> Voldoet aan minimum eis</li>
                          <li><strong>A+++:</strong> Extra bonus van €100 mogelijk</li>
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-semibold mb-2">Hogere vermogens (&gt;70kW):</h4>
                        <p className="text-sm text-muted-foreground">
                          Voor cascade-installaties tot 500 kW kan via documentatie en rekentool 
                          toch subsidie worden aangevraagd.
                        </p>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="meldcode-check">
                  <AccordionTrigger className="text-left">
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary" className="text-xs bg-amber-100 text-amber-800">Belangrijk</Badge>
                      Wat is een meldcode en hoe check ik deze?
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="space-y-4">
                    <div className="space-y-3">
                      <p>
                        Een meldcode is een unieke code per apparaat die aangeeft dat het voldoet aan ISDE eisen.
                      </p>
                      <Alert>
                        <AlertTriangle className="h-4 w-4" />
                        <AlertDescription>
                          <strong>Geen meldcode?</strong> Dan moet je uitgebreide technische documentatie meesturen. 
                          Dit verhoogt de kans op afwijzing aanzienlijk.
                        </AlertDescription>
                      </Alert>
                      <div>
                        <h4 className="font-semibold mb-2">Hoe te checken:</h4>
                        <ol className="list-decimal list-inside space-y-1 text-sm text-muted-foreground ml-4">
                          <li>Vraag meldcode bij uw installateur</li>
                          <li>Check de officiele RVO meldcodelijst</li>
                          <li>Zorg voor productdocumentatie als backup</li>
                        </ol>
                      </div>
                    </div>
                    <Button variant="outline" size="sm" asChild>
                      <a href="https://www.rvo.nl/subsidies-financiering/isde/woningeigenaren" target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="h-4 w-4 mr-2" />
                        RVO Meldcodelijst
                      </a>
                    </Button>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="isolatie-voorwaarden">
                  <AccordionTrigger className="text-left">
                    <div className="flex items-center gap-2">
                      <Badge variant="default" className="text-xs">Isolatie</Badge>
                      Welke isolatie komt in aanmerking voor subsidie?
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="space-y-4">
                    <Alert>
                      <AlertTriangle className="h-4 w-4" />
                      <AlertDescription>
                        <strong>Belangrijk:</strong> Voer eerst de isolatie uit, vraag daarna pas subsidie aan. 
                        Minimum oppervlakte is 3 m² (2025 regels).
                      </AlertDescription>
                    </Alert>
                    <div className="space-y-3">
                      <div>
                        <h4 className="font-semibold text-success mb-2">✓ Wel in aanmerking:</h4>
                        <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground ml-4">
                          <li>Bestaande muren, daken, vloeren</li>
                          <li>HR++ en Triple glas (apart subsidieerbaar)</li>
                          <li>Isolatie van bestaande leidingen</li>
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-semibold text-destructive mb-2">✗ Niet in aanmerking:</h4>
                        <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground ml-4">
                          <li>Aanbouw of nieuwe verdieping</li>
                          <li>Recreatiedelen (bijv. poolhouse)</li>
                          <li>Zelfstandige uitvoering</li>
                          <li>Eerder geïsoleerde oppervlaktes</li>
                        </ul>
                      </div>
                      <p className="text-sm font-medium">
                        <strong>Vereist:</strong> Uitvoering door erkend installatiebedrijf
                      </p>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="warmtenet-aansluiting">
                  <AccordionTrigger className="text-left">
                    <div className="flex items-center gap-2">
                      <Badge variant="default" className="text-xs">Warmtenet</Badge>
                      Wat zijn de voorwaarden voor warmtenet aansluiting?
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="space-y-4">
                    <div className="bg-success/10 p-4 rounded-lg">
                      <h4 className="font-semibold text-success mb-2">Subsidiebedrag: €3.775</h4>
                      <p className="text-sm">Voor aansluitingen na 1 januari 2024 (voorheen €3.325)</p>
                    </div>
                    <div className="space-y-3">
                      <h4 className="font-semibold mb-2">Vereiste bewijsstukken:</h4>
                      <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground ml-4">
                        <li><strong>Aangepaste meterkast</strong> (foto vereist)</li>
                        <li><strong>Gasmeter verwijderd</strong> (bewijs van demontage)</li>
                        <li><strong>Factuur aansluiting</strong> warmtenet</li>
                        <li><strong>Bewijs verwijdering</strong> gasmeter</li>
                      </ul>
                      <Alert>
                        <AlertTriangle className="h-4 w-4" />
                        <AlertDescription>
                          Zonder complete documentatie wordt uw aanvraag afgewezen. 
                          Zorg voor foto's voor, tijdens en na de werkzaamheden.
                        </AlertDescription>
                      </Alert>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="algemene-voorwaarden">
                  <AccordionTrigger className="text-left">
                    <div className="flex items-center gap-2">
                      <Badge variant="default" className="text-xs">Algemeen</Badge>
                      Wat zijn de algemene ISDE voorwaarden?
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="space-y-4">
                    <div className="space-y-4">
                      <div>
                        <h4 className="font-semibold mb-2">Woningeigenaar vereisten:</h4>
                        <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground ml-4">
                          <li><strong>Eigenaar én bewoner</strong> van de woning</li>
                          <li><strong>Woning ouder dan 1 januari 2019</strong></li>
                          <li>Bij verhuur/VvE: vaak andere regeling (SVVE)</li>
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-semibold mb-2">Aanvraag termijnen:</h4>
                        <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground ml-4">
                          <li><strong>Binnen 24 maanden</strong> na voltooiing installatie</li>
                          <li>Bewijzen: factuur, betaaldatum, foto's</li>
                          <li>Let op: 2024 maatregelen hebben mogelijk andere tarieven</li>
                        </ul>
                      </div>
                      <Alert>
                        <AlertTriangle className="h-4 w-4" />
                        <AlertDescription>
                          <strong>Bewaar altijd uw aankoopbewijs uit 2024</strong> indien van toepassing. 
                          Voor verschillende jaren kunnen verschillende tarieven gelden.
                        </AlertDescription>
                      </Alert>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="vve-verhuur">
                  <AccordionTrigger className="text-left">
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary" className="text-xs">VvE/Verhuur</Badge>
                      Wat geldt er voor VvE's en verhuurders?
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="space-y-4">
                    <Alert>
                      <AlertTriangle className="h-4 w-4" />
                      <AlertDescription>
                        Voor woningverhuur via BV of VvE geldt meestal de <strong>SVVE regeling</strong>, 
                        niet de ISDE regeling voor particulieren.
                      </AlertDescription>
                    </Alert>
                    <div className="space-y-3">
                      <div>
                        <h4 className="font-semibold mb-2">ISDE particulieren:</h4>
                        <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground ml-4">
                          <li>Eigenaar-bewoners</li>
                          <li>Particuliere eigendom</li>
                          <li>Geen commerciële verhuur</li>
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-semibold mb-2">SVVE regeling:</h4>
                        <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground ml-4">
                          <li>Verenigingen van Eigenaren</li>
                          <li>Commerciële verhuurders</li>
                          <li>BV eigendom</li>
                        </ul>
                      </div>
                    </div>
                    <Button variant="outline" size="sm" asChild>
                      <a href="https://www.rvo.nl/subsidies-financiering/svve" target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="h-4 w-4 mr-2" />
                        Info over SVVE regeling
                      </a>
                    </Button>
                  </AccordionContent>
                </AccordionItem>

              </Accordion>
            </CardContent>
          </Card>

          {/* CTA Section */}
          <div className="mt-12 text-center">
            <Card className="bg-primary/5 border-primary/20">
              <CardContent className="pt-6">
                <h3 className="text-xl font-semibold mb-4">
                  Heeft u nog vragen over de ISDE subsidie?
                </h3>
                <p className="text-muted-foreground mb-6">
                  Onze experts helpen u graag met een persoonlijk advies en begeleiding 
                  bij uw subsidieaanvraag.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button size="lg">
                    Gratis Adviesgesprek
                  </Button>
                  <Button variant="outline" size="lg" asChild>
                    <a href="https://www.rvo.nl/subsidies-financiering/isde" target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Officiële RVO Info
                    </a>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};